import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { cn } from '@rocco/utils/cn';
import { RoccoLink } from '@rocco/components/link';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { DetailSummaryRow, stripHtml } from '~/ui-rocco/pages/detail-shared';
import { ProfessionalMetaRow } from './ProfessionalMetaRow';

/** How much of the statement shows before Read more. */
const STATEMENT_LINES = 3;

/**
 * The statement, cut at three lines, with Read more under it.
 *
 * A plain clamp rather than the shared sentence crop: that crop keeps whole
 * sentences, and a practice whose opening sentence runs seven lines got seven
 * — the whole column the facts were meant to share. Three lines, wherever the
 * third ends, with the ellipsis saying there is more. Read more is offered
 * only when there is: the clamp is measured, and copy that fits shows none.
 *
 * Also the phone's, in the identity block under the tabs.
 */
export const ClampedStatement = ({
    text,
    href,
    className,
    toggleClassName,
}: {
    text: string;
    href: string;
    className: string;
    toggleClassName: string;
}) => {
    const ref = useRef<HTMLParagraphElement>(null);
    const [overflows, setOverflows] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const measure = () =>
            setOverflows(el.scrollHeight > el.clientHeight + 1);
        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [text]);

    return (
        <div>
            <p
                ref={ref}
                className={cn('m-0 overflow-hidden', className)}
                style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: STATEMENT_LINES,
                }}
            >
                {text}
            </p>
            {overflows ? (
                <RoccoLink
                    to={href}
                    preventScrollReset
                    className={cn(
                        'mt-[0.5rem] inline-block no-underline',
                        className,
                        toggleClassName,
                    )}
                >
                    Read more
                </RoccoLink>
            ) : null}
        </div>
    );
};

/**
 * The profile's summary, opening as the product's does: the statement on the
 * left, cut at three lines. The ways in — Visit Website and Save — sit level
 * with it on the right, but belong to `ProfessionalStickyCtas`, the
 * page-level pair that docks into the tab band. The product page's meta row
 * follows underneath: services as tags, then locations and associations.
 *
 * Sits under the trail in the persistent layout, so the banner and this
 * block are identical on every tab and neither re-renders when a tab changes.
 *
 * Desktop only. Mobile keeps the social-profile header, which already carries
 * the identity and CTAs in a layout built for the narrow column.
 */
export const ProfessionalProfileSummarySection = () => {
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    const initial = layoutData?.initial;
    if (!initial) return null;

    // Overview only. This block is the profile's opening statement — the
    // practice said in a paragraph, with the facts under it — and Overview is
    // where that belongs. On About it is read twice over, once here and again
    // in the section immediately below; on Products and Projects it stands
    // between the reader and the grid they came for.
    //
    // Compared against whichever tree is mounted. Hardcoding the public path
    // meant the test never matched inside the Business Manager preview, so
    // the statement and the facts under it were missing from the preview
    // altogether — the one place an author checks what they just typed.
    const base = professionalTabBase(initial.URLSegment, pathname);
    const currentPath = pathname.replace(/\/+$/, '');
    if (currentPath !== base) return null;

    const summary = initial.BlockSummary
        ? stripHtml(initial.BlockSummary)
        : undefined;

    return (
        <div className="max-md:hidden">
            {/* The sticky pair above carries the product page's 80px
                between the trail's line and this row, so the statement
                opens level with it. */}
            <DetailSummaryRow
                description={
                    summary ? (
                        <ClampedStatement
                            text={summary}
                            href={`${base}/info`}
                            // The product directory's SEO statement, to
                            // the decimal: the two are the same kind of
                            // opening line and read as one voice.
                            className="font-sans text-[#323232] md:text-[2.0625rem] md:leading-[1.25] md:font-medium md:tracking-[-0.028em]"
                            toggleClassName="text-[#8f8f8f] transition-colors hover:text-[#8f8f8f]/60"
                        />
                    ) : undefined
                }
                descriptionClassName="max-md:mt-0 md:mt-0 md:max-w-[60rem]"
                // The CTAs on this line are the sticky pair above.
                aside={null}
            />
            {/* 64px under the statement, as the other pages set their meta
                row, and 76px above the band that follows. */}
            <div className="bg-ground md:pt-[4rem] md:pb-[4.75rem]">
                <ProfessionalMetaRow />
            </div>
        </div>
    );
};
